import { Card, DatePicker, Result, Row, Space, Spin, Table, Tag } from 'antd';
import classNames from 'classnames/bind';
import { ResponsiveBar } from '@nivo/bar';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { HeadingH1 } from 'components/ui';
import API from 'helpers/API';
import { getOrderDisplayNo, getOrderStatus } from 'utils/CodeMapper';
import { getApiErrorMessage } from 'utils/getApiErrorMessage';

import styles from './orders.module.scss';
import {
  buildOrderChartData,
  buildOrderStatusSummary,
  CUSTOMER_ORDER_STATUS_CARDS,
  filterOrdersByDate,
  getRecentOrders,
} from './orders.helpers';

dayjs.extend(isSameOrBefore);

const cx = classNames.bind(styles);

const Page = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState();
  const [dates, setDates] = useState({
    from: dayjs().startOf('month'),
    to: dayjs(),
  });
  const [selectedDate, setSelectedDate] = useState();

  useEffect(() => {
    let isMounted = true;

    const fetchOrders = async () => {
      setIsLoading(true);
      setFetchError(undefined);

      const response = await API.get('/customer/orders');

      if (!isMounted) return;

      if (response?.success) {
        setOrders(response.data?.orders ?? []);
      } else {
        setOrders([]);
        setFetchError(getApiErrorMessage(response, '주문 현황을 불러오지 못했습니다.'));
      }

      setIsLoading(false);
    };

    fetchOrders();

    return () => {
      isMounted = false;
    };
  }, []);

  const summary = useMemo(() => buildOrderStatusSummary(orders), [orders]);
  const recentOrders = useMemo(() => getRecentOrders(orders, 10), [orders]);
  const chartData = useMemo(() => buildOrderChartData(orders, dates.from, dates.to), [orders, dates]);
  const selectedOrders = useMemo(() => filterOrdersByDate(orders, selectedDate), [orders, selectedDate]);

  const recentColumns = [
    {
      title: '주문번호',
      dataIndex: 'orderNo',
      render: (_, record) => getOrderDisplayNo(record),
    },
    {
      title: '주문일자',
      dataIndex: 'orderDate',
      width: 120,
      align: 'center',
    },
    {
      title: '수량',
      dataIndex: 'quantity',
      width: 80,
      align: 'center',
    },
    {
      title: '상태',
      dataIndex: 'status',
      width: 100,
      align: 'center',
      render: (value) => <Tag>{getOrderStatus(value)}</Tag>,
    },
    {
      title: '수신자명',
      dataIndex: 'deliveryName',
      width: 140,
    },
  ];

  const detailColumns = [
    {
      title: '주문번호',
      dataIndex: 'orderNo',
      render: (_, record) => getOrderDisplayNo(record),
    },
    {
      title: '상태',
      dataIndex: 'status',
      width: 100,
      align: 'center',
      render: (value) => getOrderStatus(value),
    },
    {
      title: '수량',
      dataIndex: 'quantity',
      width: 80,
      align: 'center',
    },
    {
      title: '수신자명',
      dataIndex: 'deliveryName',
      width: 140,
    },
    {
      title: '주소',
      render: (_, record) => `${record.deliveryAddress} ${record.deliveryAddressDetail ?? ''}`,
    },
  ];

  if (isLoading) {
    return (
      <Space direction="vertical" align="center" size={16} style={{ width: '100%', padding: '80px 0' }}>
        <Spin size="large" />
      </Space>
    );
  }

  if (fetchError) {
    return (
      <Result
        status="error"
        title="주문 현황을 불러오지 못했습니다."
        subTitle={fetchError}
      />
    );
  }

  return (
    <div className={cx('container')}>
      <HeadingH1 title="주문 현황" />
      <Row align="middle" justify="space-between">
        <p className={cx('time')}>{dayjs().format('YYYY년 M월 D일 HH:mm 기준')}</p>
      </Row>

      <div className={cx('cards')}>
        {CUSTOMER_ORDER_STATUS_CARDS.map((card) => (
          <Card key={card.key} size="small" className={cx('card')}>
            <div className={cx('cardTitle')}>{card.title}</div>
            <div className={cx('cardValue')}>{summary[card.key] ?? 0}</div>
          </Card>
        ))}
      </div>

      <div className={cx('panel')}>
        <div className={cx('panelTitle')}>최근 주문 10건</div>
        <Table
          style={{ marginTop: 16 }}
          rowKey="rid"
          columns={recentColumns}
          dataSource={recentOrders}
          pagination={false}
          onRow={(record) => ({
            onClick: () => navigate(`/customer/orders/${record.rid}`),
          })}
        />
      </div>

      <div className={cx('panel')}>
        <Row align="middle" justify="space-between">
          <div className={cx('panelTitle')}>일자별 주문 수</div>
          <Space>
            <DatePicker
              allowClear={false}
              value={dates.from}
              onChange={(date) => {
                setSelectedDate(undefined);
                if (date) setDates((prev) => ({ ...prev, from: date }));
              }}
            />
            ~
            <DatePicker
              allowClear={false}
              value={dates.to}
              onChange={(date) => {
                setSelectedDate(undefined);
                if (date) setDates((prev) => ({ ...prev, to: date }));
              }}
            />
          </Space>
        </Row>

        <div className={cx('chartWrapper')}>
          <ResponsiveBar
            data={chartData}
            keys={['count']}
            indexBy="label"
            margin={{ top: 40, right: 40, bottom: 64, left: 64 }}
            padding={0.4}
            colors={{ scheme: 'set1' }}
            labelTextColor="white"
            valueScale={{ type: 'linear' }}
            axisLeft={{
              legend: '주문 수',
              legendPosition: 'middle',
              legendOffset: -40,
              tickSize: 1,
            }}
            axisBottom={{
              legend: '일자',
              legendPosition: 'middle',
              legendOffset: 40,
            }}
            onClick={(event) => setSelectedDate(event.data.date)}
          />
        </div>

        {selectedDate && (
          <>
            <div className={cx('panelTitle')} style={{ marginTop: 24 }}>
              {dayjs(selectedDate).format('M월 D일')} 주문내역
            </div>
            <Table
              style={{ marginTop: 16 }}
              rowKey="rid"
              columns={detailColumns}
              dataSource={selectedOrders}
              pagination={false}
              onRow={(record) => ({
                onClick: () => navigate(`/customer/orders/${record.rid}`),
              })}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default Page;
